import { ColorEnum } from '@/constants/color'
import { Layout } from '@/features/layout/types'
import ShiftCloseoutActionCard from '@/features/shiftCloseouts/components/CreateShiftCloseoutPrompt/parts/ShiftCloseoutActionCard'
import ShiftCloseoutHistory from '@/features/shiftCloseouts/components/CreateShiftCloseoutPrompt/parts/ShiftCloseoutHistory'
import { ShiftCloseoutHistoryItem } from '@/features/shiftCloseouts/types'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface CreateShiftCloseoutPromptProps {
    history: ShiftCloseoutHistoryItem[]
    layout: Layout
    hasOpenOrders: boolean
    onCashTransactions: () => void
    onCreate: () => void
}

const CreateShiftCloseoutPrompt: FC<CreateShiftCloseoutPromptProps> = ({
    history,
    layout,
    hasOpenOrders,
    onCashTransactions,
    onCreate,
}) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, { layout })
    const title = (
        <Typography.Heading variant="h2" style={styles.title}>
            {t('shiftCloseouts.overviewTitle')}
        </Typography.Heading>
    )

    return (
        <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
        >
            {layout === 'compact' && <View style={styles.compactHeader}>{title}</View>}

            <View style={styles.content}>
                <View style={styles.actionColumn}>
                    {layout === 'extended' && <View style={styles.actionHeader}>{title}</View>}

                    <View style={styles.actions}>
                        <ShiftCloseoutActionCard
                            action={t('shiftCloseouts.create.action')}
                            description={t(
                                hasOpenOrders
                                    ? 'shiftCloseouts.create.openOrdersDescription'
                                    : 'shiftCloseouts.create.readyDescription'
                            )}
                            layout={layout}
                            onPress={onCreate}
                        />

                        <ShiftCloseoutActionCard
                            action={t('shiftCloseouts.cashTransactions.action')}
                            description={t('shiftCloseouts.cashTransactions.description')}
                            layout={layout}
                            onPress={onCashTransactions}
                        />
                    </View>
                </View>

                <View style={styles.historyColumn}>
                    <View style={styles.historyHeader}>
                        <Typography.Heading variant="h5" style={styles.historyHeading}>
                            {t('shiftCloseouts.history.heading')}
                        </Typography.Heading>
                    </View>

                    <ShiftCloseoutHistory items={history} layout={layout} />
                </View>
            </View>
        </ScrollView>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    scrollContent: {
        flexGrow: 1,
        paddingBottom: insets.bottom,
    },
    compactHeader: {
        paddingTop: 12,
        paddingLeft: 12 + insets.left,
        paddingRight: 12 + insets.right,
    },
    title: {
        color: ColorEnum.GRAY_900,
    },
    content: {
        flex: 1,
        width: '100%',
        variants: {
            layout: {
                compact: {
                    flexDirection: 'column',
                    gap: 32,
                    paddingTop: 24,
                },
                extended: {
                    flexDirection: 'row',
                },
            },
        },
    },
    actionColumn: {
        variants: {
            layout: {
                compact: {
                    width: '100%',
                    paddingLeft: 12 + insets.left,
                    paddingRight: 12 + insets.right,
                },
                extended: {
                    width: 440,
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: 16,
                },
            },
        },
    },
    actionHeader: {
        width: '100%',
        height: 40,
    },
    actions: {
        width: '100%',
        gap: 16,
    },
    historyColumn: {
        gap: 8,
        variants: {
            layout: {
                compact: {
                    width: '100%',
                    paddingBottom: 12,
                    paddingLeft: 12 + insets.left,
                    paddingRight: 12 + insets.right,
                },
                extended: {
                    flex: 1,
                    minWidth: 0,
                    padding: 16,
                    borderLeftColor: ColorEnum.GRAY_200,
                    borderLeftWidth: 1,
                },
            },
        },
    },
    historyHeading: {
        color: ColorEnum.GRAY_900,
    },
    historyHeader: {
        variants: {
            layout: {
                compact: {},
                extended: {
                    height: 40,
                    justifyContent: 'flex-end',
                },
            },
        },
    },
}))

export default CreateShiftCloseoutPrompt
